import {Navigate} from 'react-router'
import includes from 'lodash/includes'
import {useAsyncOnceDefined} from '@freckle/educator-entities/ts/common/hooks/use-async/index'
import {useCourseMemberships} from '@freckle/educator-entities/ts/common/hooks/use-course-memberships'
import {useCourses} from '@freckle/educator-entities/ts/common/hooks/use-courses'
import type {RlDomainT} from '@freckle/educator-entities/ts/common/models/rl-domain'
import type {RlStandardWithDomainT} from '@freckle/educator-entities/ts/common/models/rl-standard'
import {safeExtractDomains} from '@freckle/educator-entities/ts/common/models/rl-standard'
import {fetchDokQuestionStandardPairs} from '@freckle/educator-entities/ts/math/common/models/dok-question-standard-pair'
import type {DokQuestionStandardPairT} from '@freckle/educator-entities/ts/math/common/models/dok-question-standard-pair'
import {ShareAssignmentErrorModalWrapper} from '@freckle/classroom/ts/common/components/share-assignment-error-modal'
import {WithResources} from '@freckle/classroom/ts/common/components/with-resources'
import {areAllStudentsInCourse} from '@freckle/classroom/ts/common/helpers/handlers'
import {Routes} from '@freckle/classroom/ts/common/routers/routes'
import {useMathStandardSet, useMathStandards} from '@freckle/classroom/ts/hooks/alignment'
import {useClaimedFreeResourcesForCurrentWeek} from '@freckle/classroom/ts/hooks/use-claimed-free-resources'
import {useCourseSubjectMap} from '@freckle/classroom/ts/hooks/use-course-subject-map'
import {useCurrentCourseId} from '@freckle/classroom/ts/hooks/use-current-course-id'
import {useQueryParamAsString} from '@freckle/classroom/ts/hooks/use-params'
import {useRenaissanceNextContext} from '@freckle/classroom/ts/hooks/use-renaissance-next-context'
import {useStudents} from '@freckle/classroom/ts/hooks/use-students'
import {useStudentsQueryParam} from '@freckle/classroom/ts/hooks/use-students-query-param'
import {useTeacher} from '@freckle/classroom/ts/hooks/use-teacher'
import {TargetedDepthOfKnowledgePracticeAssignmentsContainer} from '../targeted-depth-of-knowledge-practice-assignments/container'
import {chromelessDOKContainer} from './chromeless-dok-assignments.module.scss'

export default function ChromelessMathTargetedDepthOfKnowledgePracticeAssignmentsHandler() {
  const {unifiedAssignmentId} = useRenaissanceNextContext()
  const initialCourseId = useCurrentCourseId()
  const selectedDomainId = useQueryParamAsString('domainId') ?? null
  const selectedStandardId = useQueryParamAsString('standardId') ?? null
  const selectedStudentIds = useStudentsQueryParam()

  const teacher = useTeacher()
  const mathStandardSet = useMathStandardSet()
  const students = useStudents()
  const courseMemberships = useCourseMemberships()
  const courses = useCourses()
  const rlStandards = useMathStandards()
  const dokQuestionStandardPairs = useAsyncOnceDefined(
    ({teacher}) => fetchDokQuestionStandardPairs(teacher.mathStandardSetId),
    'fetchDokQuestionStandardPairs',
    [teacher.data?.mathStandardSetId],
    {teacher},
  )
  const claimedFreeResources = useClaimedFreeResourcesForCurrentWeek()
  const courseSubjectMap = useCourseSubjectMap({
    tag: 'active-courses-or',
    courseId: initialCourseId,
  })

  return (
    <WithResources
      resources={{
        teacher,
        mathStandardSet,
        students,
        courseMemberships,
        courses,
        rlStandards,
        dokQuestionStandardPairs,
        claimedFreeResources,
        courseSubjectMap,
      }}
      render={({
        teacher,
        mathStandardSet,
        students,
        courseMemberships,
        courses,
        rlStandards,
        dokQuestionStandardPairs,
        claimedFreeResources,
        courseSubjectMap,
      }) => {
        if (
          !areAllStudentsInCourse(students, courseMemberships, initialCourseId, selectedStudentIds)
        ) {
          return <Navigate to={Routes.math.assignTargeted({})} replace />
        }

        const [domainsWithDok, dokStandards] = getDomainsAndStandardsWithDok(
          rlStandards,
          dokQuestionStandardPairs,
        )

        return (
          <ShareAssignmentErrorModalWrapper
            shareError={
              selectedStandardId !== null && !rlStandards.find(s => s.id === selectedStandardId)
            }
          >
            <div className={chromelessDOKContainer}>
              <TargetedDepthOfKnowledgePracticeAssignmentsContainer
                dokQuestionStandardPairs={dokQuestionStandardPairs}
                teacher={teacher}
                mathStandardSet={mathStandardSet}
                dokRlDomains={domainsWithDok}
                dokRlStandards={dokStandards}
                students={students}
                courseMemberships={courseMemberships}
                courses={courses}
                selectedStudentIds={selectedStudentIds}
                selectedDomainId={selectedDomainId}
                selectedStandardId={selectedStandardId}
                courseSubjectMap={courseSubjectMap}
                claimedFreeResources={claimedFreeResources}
                chromeless
                unifiedAssignmentId={unifiedAssignmentId}
              />
            </div>
          </ShareAssignmentErrorModalWrapper>
        )
      }}
    />
  )
}

function getDomainsAndStandardsWithDok(
  rlStandards: Array<RlStandardWithDomainT>,
  dokQuestionStandardPairs: Array<DokQuestionStandardPairT>,
): [Array<RlDomainT>, Array<RlStandardWithDomainT>] {
  const standardIdsWithDok = dokQuestionStandardPairs.map(qs => qs.standardId)
  const dokStandards = rlStandards.filter(std => includes(standardIdsWithDok, std.id))

  return [safeExtractDomains(dokStandards), dokStandards]
}
